/** @jsx jsx */
import { gql, useQuery } from '@apollo/client';
import {
  Badge,
  CardDescription,
  CardHeader,
  CardTitle,
  Text,
} from '@apollo/orbit';
import { jsx } from '@emotion/react';
import { sum } from 'lodash';
import { useCallback, useMemo } from 'react';

import { useStatsVariables } from 'src/app/account/championDashboardPage/useStatsVariables';
import {
  ChampionDashboardCard,
  ChampionDashboardCardBody,
} from 'src/app/account/championDashboardPage/widgets/ChampionDashboardCard';
import { EmptyLayout } from 'src/app/account/championDashboardPage/widgets/EmptyLayout';
import { BarChart } from 'src/components/charts/BarChart/BarChart';
import { CursorProvider } from 'src/components/charts/helpers/Cursor';
import { Loading } from 'src/components/common/loading/Loading';
import { format } from 'src/lib/format';
import {
  ChecksWidgetQuery,
  ChecksWidgetQueryVariables,
  ChecksWidgetQuery_account_registryStatsWindow_schemaCheckStats,
} from 'src/lib/graphqlTypes/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { DocsRouter } from 'src/lib/routers';

const range = 60;
const stableEmptyArray: NonNullable<
  NonNullable<ChecksWidgetQuery['account']>['registryStatsWindow']
>['schemaCheckStats'] = [];

export const ChecksWidget = ({ accountId }: { accountId: string }) => {
  const { timeFrom, timeTo } = useStatsVariables(60);

  const { data, loading } = useQuery<
    ChecksWidgetQuery,
    ChecksWidgetQueryVariables
  >(
    gql`
      query ChecksWidgetQuery(
        $accountId: ID!
        $timeFrom: Timestamp!
        $timeTo: Timestamp!
      ) {
        account(id: $accountId) {
          id
          registryStatsWindow(from: $timeFrom, to: $timeTo) {
            schemaCheckStats {
              id
              timestamp
              metrics {
                totalFailedChecks
                totalSuccessfulChecks
              }
            }
          }
        }
      }
    `,
    {
      variables: {
        accountId,
        timeFrom,
        timeTo,
      },
    },
  );
  const checksStats =
    data?.account?.registryStatsWindow?.schemaCheckStats ?? stableEmptyArray;

  const getValue = useCallback(
    (d: ChecksWidgetQuery_account_registryStatsWindow_schemaCheckStats) => ({
      timestamp: d.timestamp,
      value: d.metrics.totalSuccessfulChecks + d.metrics.totalFailedChecks,
      tooltipContent: `${d.metrics.totalSuccessfulChecks} passed | ${d.metrics.totalFailedChecks} failed`,
      stackCounts: [
        { value: d.metrics.totalSuccessfulChecks },
        { value: d.metrics.totalFailedChecks },
      ],
    }),
    [],
  );

  const totalChecks = useMemo(() => {
    return checksStats.reduce((total, { metrics }) => {
      return total + metrics.totalSuccessfulChecks + metrics.totalFailedChecks;
    }, 0);
  }, [checksStats]);

  const averageFailed = useMemo(
    () =>
      Math.ceil(
        (sum(
          checksStats.map(
            ({ metrics: { totalFailedChecks, totalSuccessfulChecks } }) => {
              const total = totalSuccessfulChecks + totalFailedChecks;
              return total === 0 ? 0 : totalFailedChecks / total;
            },
          ),
        ) /
          checksStats.length) *
          100,
      ),
    [checksStats],
  );

  return (
    <ChampionDashboardCard className="col-span-full xl:col-span-3">
      <CardHeader>
        <CardTitle>
          Checks <Badge variant="info">{range} days</Badge>
        </CardTitle>
        {!loading && totalChecks > 0 && (
          <CardDescription>
            <Text as="span" variant="bodySb" className="text-secondary">
              {format.number(totalChecks, '0[.]0a')}
            </Text>{' '}
            total checks /{' '}
            <Text as="span" variant="bodySb" className="text-error">
              {averageFailed}%
            </Text>{' '}
            avg rate of failed checks
          </CardDescription>
        )}
      </CardHeader>
      <ChampionDashboardCardBody>
        {loading ? (
          <Loading />
        ) : isMinLengthArray(1, checksStats) && totalChecks > 0 ? (
          <CursorProvider>
            <BarChart
              className="h-full"
              data={checksStats}
              getValue={getValue}
            />
          </CursorProvider>
        ) : (
          <EmptyLayout
            title="Enable and run schema checks"
            body="Use schema checks to protect your clients and ensure you never ship a schema with breaking changes."
            learnMoreHref={DocsRouter.path('SchemaValidation')}
          />
        )}
      </ChampionDashboardCardBody>
    </ChampionDashboardCard>
  );
};
